import { useEffect, useMemo, useState } from 'react'
import { gql, useQuery } from 'urql'
import { HOST_LISTINGS_MANAGEMENT_SERVICE } from '../../Constants'
import { VenueRowSpace } from '../../Components/Recommendations/VenueRow'
import captureMessage from '../../Utilities/Errors/captureMessage'
import {
  getShortlistParam,
  getStoredShortlist,
  setStoredShortlist,
} from '../../Utilities/Storage/shortlist'

// Favourites are created against a host, which the shortlist ids alone cannot tell us.
export interface ShortlistSpace extends VenueRowSpace {
  hostID: number
}

type ShortlistedSpace = {
  id: number
  spaceName?: string | null
  userId?: number | null
  status?: string | null
  deleted?: boolean | null
  spacePhotos?: { url: string; position?: number | null }[] | null
  venue?: { name?: string | null; city?: string | null; country?: string | null } | null
}

type ShortlistedSpacesQuery = Record<string, ShortlistedSpace | null>

const alias = (spaceId: number): string => `space${spaceId}`

// `spaces(filter: { ids })` is host-only, so the ids are aliased into one public request instead.
const buildQuery = (spaceIds: number[]) => gql`
  query ShortlistedSpaces {
    ${spaceIds.map((id) => `${alias(id)}: spaceById(id: ${id}) { ...ShortlistedSpace }`).join('\n')}
  }

  fragment ShortlistedSpace on Space {
    id
    spaceName
    userId
    status
    deleted
    spacePhotos {
      id
      url
      position
    }
    venue {
      name
      city
      country
    }
  }
`

const NO_SPACES = gql`
  query ShortlistedSpaces {
    __typename
  }
`

const toShortlistSpace = (space: ShortlistedSpace): ShortlistSpace | null => {
  if (space.deleted || space.status !== 'published' || !space.userId) return null

  return {
    id: String(space.id),
    title: space.spaceName?.trim() ?? '',
    venueName: space.venue?.name?.trim() ?? '',
    images: [...(space.spacePhotos ?? [])]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map(({ url }) => url),
    location: {
      city: space.venue?.city?.trim() ?? '',
      country: space.venue?.country?.trim() ?? '',
    },
    hostID: space.userId,
  }
}

export const useShortlistIds = (): number[] => {
  const [spaceIds] = useState<number[]>(() => {
    const fromURL = getShortlistParam(new URLSearchParams(window.location.search))
    if (!fromURL.length) return getStoredShortlist()

    setStoredShortlist(fromURL)
    return fromURL
  })

  return spaceIds
}

interface UseShortlistedSpaces {
  spaceIds: number[]
  spaces: ShortlistSpace[]
  fetching: boolean
}

export const useShortlistedSpaces = (
  fallbacks: ShortlistSpace[] = [],
  skip = false
): UseShortlistedSpaces => {
  const spaceIds = useShortlistIds()
  const query = useMemo(() => (spaceIds.length ? buildQuery(spaceIds) : NO_SPACES), [spaceIds])

  const [{ fetching, error, data }] = useQuery<ShortlistedSpacesQuery>({
    query,
    pause: skip || !spaceIds.length,
    context: useMemo(
      () => ({
        requestPolicy: 'cache-first',
        url: HOST_LISTINGS_MANAGEMENT_SERVICE,
      }),
      []
    ),
  })

  useEffect(() => {
    if (error) captureMessage(error.message)
  }, [error])

  const spaces = useMemo(() => {
    if (skip) return []

    const resolve = (spaceId: number): ShortlistSpace | null => {
      const space = data?.[alias(spaceId)]
      const mapped = space ? toShortlistSpace(space) : null

      // An unresolvable id is still worth showing when the recommendations describe it.
      return mapped ?? fallbacks.find(({ id }) => Number(id) === spaceId) ?? null
    }

    return spaceIds.map(resolve).filter((space): space is ShortlistSpace => space !== null)
  }, [skip, spaceIds, data, fallbacks])

  return { spaceIds, spaces, fetching: !skip && !!spaceIds.length && fetching }
}
